import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, AlertTriangle, Plus, Loader2 } from "lucide-react";

export const Route = createFileRoute("/teacher/behaviour")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Behaviour Tracking · MAKAI Teachers" }] }),
  component: Page,
});

type Incident = {
  id: string; learner_id: string; incident_date: string; category: string;
  severity: string; description: string; action_taken: string | null; resolved: boolean;
  learner: { first_name: string; last_name: string } | null;
};

function Page() {
  const ctx = useTeacherContext();
  const [learners, setLearners] = useState<{ id: string; first_name: string; last_name: string; stream_id: string | null }[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [learnerId, setLearnerId] = useState("");
  const [category, setCategory] = useState("Positive recognition");
  const [severity, setSeverity] = useState("minor");
  const [description, setDescription] = useState("");
  const [action, setAction] = useState("");

  useEffect(() => {
    if (!ctx.schoolId || ctx.streams.length === 0) { setLoading(false); return; }
    const streamIds = ctx.streams.map((s) => s.id);
    (async () => {
      const [{ data: ls }, { data: incs }] = await Promise.all([
        supabase.from("learners")
          .select("id, first_name, last_name, current_stream_id")
          .in("current_stream_id", streamIds).eq("status", "active").order("first_name"),
        supabase.from("discipline_incidents")
          .select("id, learner_id, incident_date, category, severity, description, action_taken, resolved, learner:learners(first_name, last_name)")
          .in("learner_id", (await supabase.from("learners").select("id").in("current_stream_id", streamIds)).data?.map((l) => l.id) ?? [])
          .order("incident_date", { ascending: false }).limit(50),
      ]);
      setLearners((ls ?? []).map((l) => ({ id: l.id, first_name: l.first_name, last_name: l.last_name, stream_id: l.current_stream_id })));
      setIncidents((incs ?? []) as any);
      setLoading(false);
    })();
  }, [ctx.schoolId, ctx.streams]);

  async function log() {
    if (!learnerId || !description.trim() || !ctx.schoolId) {
      toast.error("Pick a learner and add a description"); return;
    }
    setSaving(true);
    const { data, error } = await supabase.from("discipline_incidents").insert({
      school_id: ctx.schoolId, learner_id: learnerId, category, severity: severity as any,
      description, action_taken: action || null,
    }).select("id, learner_id, incident_date, category, severity, description, action_taken, resolved, learner:learners(first_name, last_name)").single();
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    setIncidents((prev) => [data as any, ...prev]);
    setDescription(""); setAction("");
    toast.success("Logged");
  }

  async function toggleResolved(id: string, resolved: boolean) {
    const { error } = await supabase.from("discipline_incidents").update({ resolved: !resolved }).eq("id", id);
    if (error) return toast.error(error.message);
    setIncidents((prev) => prev.map((i) => i.id === id ? { ...i, resolved: !resolved } : i));
  }

  return (
    <AppShell role="teacher" title="Behaviour Tracking" subtitle="Celebrate progress · log concerns · keep parents in the loop">
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Plus className="h-4 w-4 text-primary" /> Log incident or recognition</div>
          <div>
            <Label>Learner</Label>
            <Select value={learnerId} onValueChange={setLearnerId}>
              <SelectTrigger><SelectValue placeholder="Choose learner" /></SelectTrigger>
              <SelectContent>
                {learners.map((l) => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Positive recognition">Positive recognition</SelectItem>
                  <SelectItem value="Effort & participation">Effort & participation</SelectItem>
                  <SelectItem value="Leadership">Leadership</SelectItem>
                  <SelectItem value="Disruption">Disruption</SelectItem>
                  <SelectItem value="Lateness">Lateness</SelectItem>
                  <SelectItem value="Bullying">Bullying</SelectItem>
                  <SelectItem value="Property damage">Property damage</SelectItem>
                  <SelectItem value="Uniform">Uniform</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minor">Minor / Positive</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="major">Major</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What happened?" /></div>
          <div><Label>Action taken (optional)</Label><Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Praised in class / spoke with parent…" /></div>
          <Button onClick={log} disabled={saving} className="w-full">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Save
          </Button>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 text-sm font-semibold">Recent activity</div>
          {loading ? (
            <div className="grid h-32 place-items-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : incidents.length === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No entries yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {incidents.map((i) => {
                const positive = i.category.toLowerCase().includes("positive") || i.category.toLowerCase().includes("effort") || i.category.toLowerCase().includes("leadership");
                return (
                  <li key={i.id} className="py-3">
                    <div className="grid grid-cols-[auto_1fr_auto] items-start gap-3">
                      <div className={`grid h-9 w-9 place-items-center rounded-lg ${positive ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
                        {positive ? <Heart className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">{i.learner?.first_name} {i.learner?.last_name} · <span className="text-muted-foreground">{i.category}</span></div>
                        <div className="text-sm">{i.description}</div>
                        {i.action_taken && <div className="text-xs text-muted-foreground">Action: {i.action_taken}</div>}
                        <div className="mt-1 text-[11px] text-muted-foreground">{new Date(i.incident_date).toLocaleDateString()} · {i.severity}</div>
                      </div>
                      <Button size="sm" variant={i.resolved ? "outline" : "default"} onClick={() => toggleResolved(i.id, i.resolved)}>
                        {i.resolved ? "Reopen" : "Resolve"}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
